import React, { useState } from "react";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { url } from "../../utils/confserveur";
import { CheckCircle, XCircle, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";
import BarreRecherche from "../shared/BarreRecherche";
import FiltrePills from "../shared/FiltrePills";

function StatutPassationEvalBilan({ evalBilan, onReload, setEvalBilan }) {
  const [{ isError: isErrorPost }, doPost] = usePostApi(url + "eleves_evaluations_nat/update_reponses/");

  // État pour le modal de confirmation
  const [confirmEleve, setConfirmEleve] = useState(null);
  const [isResetting, setIsResetting] = useState(null);

  // État pour la recherche
  const [searchTerm, setSearchTerm] = useState('');

  // Filtre statut
  const [filterStatut, setFilterStatut] = useState('all'); // 'all', 'fait', 'attente'

  const initResponses = async (eleveId) => {
    setIsResetting(eleveId);
    const results = {
      evaluation_id: evalBilan.id,
      eleve_id: parseInt(eleveId, 10),
      reponses: {}
    };

    await doPost(results);
    onReload();

    if (!isErrorPost) {
      const updatedEleves = evalBilan.eleves_reponses.map(eleve => {
        if (eleve.id_eleve === eleveId) {
          const updatedEleve = { ...eleve };
          delete updatedEleve.reponses;
          return updatedEleve;
        }
        return eleve;
      });
      setEvalBilan({ ...evalBilan, eleves_reponses: updatedEleves });
    }
    setIsResetting(null);
    setConfirmEleve(null);
  };

  // Filtrage des élèves
  const filteredEleves = evalBilan.eleves_reponses.filter(e => {
    const matchSearch = !searchTerm || e.nom_eleve?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut =
      filterStatut === 'all' ||
      (filterStatut === 'fait' && e.reponses) ||
      (filterStatut === 'attente' && !e.reponses);
    return matchSearch && matchStatut;
  });

  const elevesFait = evalBilan.eleves_reponses.filter(e => e.reponses).length;
  const elevesAttente = evalBilan.eleves_reponses.length - elevesFait;

  return (
    <div className="mt-4">
      {/* Barre de recherche et filtres */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <BarreRecherche
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Rechercher un élève..."
          className="flex-1 min-w-[200px]"
        />
        <FiltrePills
          value={filterStatut}
          onChange={setFilterStatut}
          options={[
            { key: 'all', label: 'Tous', count: evalBilan.eleves_reponses.length, activeColor: 'bg-blue-600' },
            { key: 'fait', label: 'Fait', count: elevesFait, activeColor: 'bg-emerald-600' },
            { key: 'attente', label: 'En attente', count: elevesAttente, activeColor: 'bg-amber-500' },
          ]}
        />
      </div>

      {/* Liste des élèves en cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
        {filteredEleves.map((e) => (
          <div
            key={e.eleve_id || e.id_eleve}
            className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
              e.reponses
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                e.reponses ? 'bg-emerald-500' : 'bg-amber-400'
              }`}>
                {e.reponses
                  ? <CheckCircle className="w-5 h-5 text-white" />
                  : <XCircle className="w-5 h-5 text-white" />
                }
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{e.nom_eleve}</p>
                <p className={`text-xs font-medium ${e.reponses ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {e.reponses ? 'A répondu' : 'En attente'}
                </p>
              </div>
            </div>
            {e.reponses && (
              <button
                onClick={() => setConfirmEleve(e)}
                disabled={isResetting === e.id_eleve}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Réinitialiser les réponses"
              >
                {isResetting === e.id_eleve
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />
                }
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Message si aucun résultat */}
      {filteredEleves.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 font-medium">Aucun élève ne correspond à votre recherche</p>
        </div>
      )}

      {/* Modal de confirmation */}
      {confirmEleve && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmEleve(null)} />
          <div className="relative z-10 w-[90vw] max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              <h2 className="font-bold text-lg">Réinitialiser les réponses ?</h2>
            </div>
            {/* Contenu */}
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Vous êtes sur le point de réinitialiser les réponses de :
              </p>
              <p className="text-lg font-bold text-gray-900 mb-4">{confirmEleve.nom_eleve}</p>
              <p className="text-sm text-gray-500 mb-6">
                L'élève devra repasser l'évaluation. Cette action est irréversible.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmEleve(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => initResponses(confirmEleve.id_eleve)}
                  disabled={isResetting}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isResetting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />
                  }
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatutPassationEvalBilan;
